import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const sizes: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

const focusRing =
  'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(128,110,225,.3)]';

/**
 * Overlay dialog primitive. Renders a `bg-black/50 backdrop-blur` overlay
 * behind a `--card` panel with a header (title + close button), body, and
 * optional footer. Closes on ESC or a click on the backdrop (clicks inside
 * the panel do not close it), and moves focus into the panel on open.
 */
const Modal: React.FC<ModalProps> = ({ open, onClose, title, size = 'md', children, footer }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Callers usually pass an inline arrow, so onClose changes on every render. Reading it
  // through a ref keeps the effect below keyed on `open` alone: if it re-ran per render,
  // its focus() would pull the caret out of any field in the dialog on each keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    panelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur animate-fade-in motion-reduce:animate-none"
        onClick={onClose}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full bg-[var(--card)] text-[var(--card-foreground)] rounded-[var(--radius-2xl)]',
          'border border-[var(--border)] shadow-[var(--shadow-lg)]',
          'animate-scale-in motion-reduce:animate-none',
          'focus-visible:outline-none',
          sizes[size]
        )}
      >
        <div className="flex items-center justify-between gap-4 px-6 pt-6 pb-2">
          {title ? (
            <h2 id="modal-title" className="text-lg font-semibold text-[var(--foreground)]">
              {title}
            </h2>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={cn(
              'inline-flex items-center justify-center rounded-md p-1 shrink-0',
              'text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]',
              'transition-colors duration-150',
              focusRing
            )}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-3 px-6 pb-6">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
